import { IBFBolus, IBF_BOLUS } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BOLUS";
import {
  IBFSuggestedCalculation,
  IBF_SUGGESTED_CALCULATION,
} from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_SUGGESTED_CALCULATION";
import { IBFLogRecord, IBFLogRecordHistory } from "@/lib/drivers/insulet/IBFRecords/IBF_LOG_RECORD";
import { OmnipodData } from "@/lib/drivers/insulet/OmnipodTypes";
import { OmnipodTransformer } from "@/lib/drivers/insulet/transformers/OmnipodTransformer";
import { BolusInsulinDTO, BolusSettingsDTO } from "@/models/InsulinModel";

type OmnipodBolusInsulinEvents = IBFBolus | IBFSuggestedCalculation;

type OmnipodBolusInsulinLog = IBFLogRecordHistory<OmnipodBolusInsulinEvents>;

type BolusEvent = {
  bolus: IBFLogRecordHistory<IBFBolus>;
  extendedBolus?: IBFLogRecordHistory<IBFBolus>;
  calculation?: IBFLogRecordHistory<IBFSuggestedCalculation>;
};

export class OmnipodBolusInsulinTransformer implements OmnipodTransformer<BolusInsulinDTO[]> {
  _is_bolus_event = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFBolus> =>
    event.type === "HISTORY" && event.history.type === IBF_BOLUS.value;

  _is_suggested_calculation_event = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFSuggestedCalculation> =>
    event.type === "HISTORY" && event.history.type === IBF_SUGGESTED_CALCULATION.value;

  bolus_insulin = (events: BolusEvent[]): BolusInsulinDTO[] => {
    const insulin: BolusInsulinDTO[] = [];

    for (const event of events) {
      const hasCarbs = !!event.calculation?.history.data.carbs;
      const foodBolus = event.calculation?.history.data.carbBolusProgrammed ?? 0;
      const foodBolusMethod = hasCarbs && foodBolus ? "carbs" : foodBolus ? "manual" : "none";
      const correctionBolus = event.calculation?.history.data.correctionProgrammed ?? 0;
      const totalBolus = foodBolus + correctionBolus;

      const percentageNow = event.extendedBolus ? 1 - event.extendedBolus.history.data.volume / totalBolus : 1;
      const iob = event.calculation
        ? event.calculation.history.data.correctionIOB + event.calculation.history.data.mealIOB
        : undefined;

      const bolusSettings: BolusSettingsDTO = {
        bolus_id: event.bolus.logIndex,
        food_insulin_input_method: foodBolusMethod,
        food_insulin: event.calculation?.history.data.carbBolusProgrammed ?? 0,
        food_insulin_suggested: event.calculation?.history.data.carbBolusSuggested,
        correction_insulin_input_method: correctionBolus ? "blood_glucose" : "none",
        correction_insulin: correctionBolus,
        correction_insulin_suggested: event.calculation?.history.data.correctionSuggested,
        total_insulin: totalBolus,

        carbs: event.calculation?.history.data.carbs,
        carbs_ratio: event.calculation?.history.data.icRatioUsed,
        glycemia: event.calculation?.history.data.currentGlycemia,
        insulin_sensitivity: event.calculation?.history.data.correctionFactorUsed,
        target_glycemia: event.calculation?.history.data.targetGlycemia,
        iob,
        percentage_delivered_now: percentageNow,
        bolus_insulin: totalBolus * percentageNow,
        bolex_insulin: totalBolus * (1 - percentageNow),
        bolex_duration: event.extendedBolus?.history.data.extendedDuration,
      };

      const bolusDelivered = event.bolus.history.data.volume;
      const bolexDelivered = event.extendedBolus?.history.data.volume ?? 0;

      const bolusRequested = bolusSettings.bolus_insulin ?? 0;
      const bolexRequested = bolusSettings.bolex_insulin ?? 0;

      const deliveredInsulin = bolusDelivered + bolexDelivered;

      const bolus: BolusInsulinDTO = {
        sequence_id: event.bolus.logIndex,
        quantity: deliveredInsulin,
        date: event.bolus.date.toISO() ?? "",
        type: "short",
        reason: "bolus",
        bolus_settings: bolusSettings,
        delivered_insulin: deliveredInsulin,
        requested_insulin: bolusRequested + bolexRequested,
      };
      insulin.push(bolus);
    }
    return insulin;
  };

  _find_calculation = (
    event: IBFLogRecordHistory<IBFBolus>,
    calculations: IBFLogRecordHistory<IBFSuggestedCalculation>[],
  ): IBFLogRecordHistory<IBFSuggestedCalculation> | undefined => {
    const calculationOffset = event.history.data.calculationRecordOffset;
    const calculationIndex = event.logIndex - calculationOffset;
    return calculations.find(calculation => calculation.logIndex === calculationIndex);
  };

  _find_bolus_extended = (
    event: IBFLogRecordHistory<IBFBolus>,
    extendedBolusEvents: IBFLogRecordHistory<IBFBolus>[],
  ): IBFLogRecordHistory<IBFBolus> | undefined => {
    // Either dates are the same, or they are contiguous.
    // Both should either be true or false, but it's a good security check.
    return extendedBolusEvents.find(bolus => bolus.date === event.date || bolus.logIndex === event.logIndex + 1);
  };

  merge_into_bolus_event = (events: OmnipodBolusInsulinLog[]): BolusEvent[] => {
    const calculations: IBFLogRecordHistory<IBFSuggestedCalculation>[] = events.filter(event =>
      this._is_suggested_calculation_event(event),
    );
    const bolusEvents: IBFLogRecordHistory<IBFBolus>[] = events
      .filter(event => this._is_bolus_event(event))
      .filter(event => event.history.data.immediateDuration);
    const extendedBolusEvents: IBFLogRecordHistory<IBFBolus>[] = events
      .filter(event => this._is_bolus_event(event))
      .filter(event => event.history.data.extendedDuration);

    return bolusEvents.map(e => ({
      bolus: e,
      extendedBolus: this._find_bolus_extended(e, extendedBolusEvents),
      calculation: this._find_calculation(e, calculations),
    }));
  };

  transform = (data: OmnipodData): BolusInsulinDTO[] => {
    const bolusEvents = data.logs.filter(e => this._is_bolus_event(e) || this._is_suggested_calculation_event(e));
    const bolusEventsGrouped = this.merge_into_bolus_event(bolusEvents);
    return this.bolus_insulin(bolusEventsGrouped);
  };
}
